import { TZDate } from '@date-fns/tz';

/**
 * Optional zone parameter accepted by the interval functions.
 *
 * - `timeZone` omitted: the functions use the runtime's local zone, exactly as the web does
 *   today (plain `Date` local-time methods).
 * - `timeZone` set: an IANA zone name. Local-time operations are evaluated in that zone
 *   (via `TZDate` from `@date-fns/tz`). Fixed offsets are rejected (see `assertIanaZone`).
 */
export type ZoneOptions = { timeZone?: string };

const FIXED_OFFSET =
  /^(Z|[+-]\d{1,2}(:?\d{2})?|(UTC|GMT)[+-]\d{1,2}(:?\d{2})?|Etc\/GMT[+-]\d{1,2})$/i;

/**
 * Throws unless `timeZone` is a real IANA zone name. Fixed UTC offsets ("-04:00", "UTC+5",
 * "Etc/GMT+5") are rejected: they are right in one DST regime and an hour wrong in the other,
 * which moves end-of-day boundary items into the neighbouring column.
 */
export function assertIanaZone(timeZone: string): void {
  if (typeof timeZone !== 'string' || timeZone.length === 0 || FIXED_OFFSET.test(timeZone)) {
    throw new Error(
      `Invalid time zone '${timeZone}': an IANA zone name (e.g. 'America/New_York') is required; fixed offsets are not supported.`,
    );
  }
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
  } catch {
    throw new Error(`Invalid time zone '${timeZone}': not a recognised IANA zone name.`);
  }
}

/**
 * Zone-preserving clone. Without a zone this is exactly `new Date(date)`; with a zone the clone
 * is a `TZDate`, whose local-time getters/setters operate in that zone.
 */
export function cloneInZone(date: Date, timeZone?: string): Date {
  if (timeZone === undefined) return new Date(date);
  assertIanaZone(timeZone);
  return new TZDate(date.getTime(), timeZone);
}

/** Converts a result back to a plain `Date` (same instant) when a zone was used. */
export function toPlainDate(date: Date, timeZone?: string): Date {
  return timeZone === undefined ? date : new Date(date.getTime());
}
